/**
 * Logging Utilities for Frontend Application
 *
 * This module provides convenient logging functions that automatically
 * detect development vs production mode and only log in development.
 *
 * Usage:
 * import { logDebug, logInfo, logWarn, logError } from '../util/logging';
 *
 * logDebug('MyComponent', 'User clicked button', { userId: 123 });
 * logInfo('Service', 'Data loaded successfully');
 * logWarn('Validation', 'Missing optional field', { field: 'description' });
 * logError('API', 'Failed to save data', error);
 */

import { getLogger, LogLevel } from "../service/loggerService";

// Get logger instance
const logger = getLogger();

/**
 * Debug level logging
 * Use for detailed diagnostic information
 */
export function logDebug(source: string, message: string, data?: any): void {
	logger.debug(source, message, data);
}

/**
 * Info level logging
 * Use for general information about application flow
 */
export function logInfo(source: string, message: string, data?: any): void {
	logger.info(source, message, data);
}

/**
 * Warning level logging
 * Use for potentially problematic situations
 */
export function logWarn(source: string, message: string, data?: any): void {
	logger.warn(source, message, data);
}

/**
 * Error level logging
 * Use for error conditions that might still allow the application to continue
 */
export function logError(source: string, message: string, data?: any): void {
	logger.error(source, message, data);
}

/**
 * Start a log group for organizing related log entries
 */
export function logGroup(source: string, title: string): void {
	logger.group(source, title);
}

/**
 * End the current log group
 */
export function logGroupEnd(): void {
	logger.groupEnd();
}

/**
 * Check if we're in development mode (for conditional logging)
 */
export function isDevelopmentMode(): boolean {
	return logger.isDevelopmentMode();
}

/**
 * Set minimum log level
 */
export function setLogLevel(level: LogLevel): void {
	logger.setLogLevel(level);
}

/**
 * Get the logger instance (for advanced usage)
 */
export function getLoggerInstance() {
	return logger;
}

/**
 * Clear log history
 */
export function clearLogHistory(): void {
	logger.clearHistory();
}

/**
 * Get log history (useful for debugging)
 */
export function getLogHistory() {
	return logger.getHistory();
}

/**
 * Dump current logger state for debugging
 */
export function dumpLoggerState(): void {
	logger.dumpState();
}

// Export LogLevel enum for convenience
export { LogLevel } from "../service/loggerService";
